import axios, {AxiosResponse} from 'axios'
import {channelHeaderInterceptor} from "@/api/interceptor/channelHeaderInterceptor";

import Product from "@/domain/product/Product";
import PromotionBanner from "@/domain/banner/PromotionBanner";
import ThemeBanner from "@/domain/banner/ThemeBanner";
import Hashtag from "@/domain/product/Hashtag";
import GetProductRequest from "@/domain/request/GetProductRequest";
import GetProductResponse from "@/domain/response/GetProductResponse";
import GetMaxGiftReceiverResponse from "@/domain/response/GetGiftReceiverLimitResponse";
import GetGiftReceiverLimitResponse from "@/domain/request/GetGiftLimitResponse";
import GetTicketResponse from "@/domain/response/GetTicketResponse";
import PageableRequest from "@/domain/request/PageableRequest";
import PromotionBannerDetail from "@/domain/banner/PromotionBannerDetail";
import GetOrderResponse from "@/domain/response/GetOrderResponse";
import {memberHeaderInterceptor} from "@/api/interceptor/memberHeaderInterceptor";
import GetTicketCancellationResponse from "@/domain/response/GetTicketCancellationResponse";
import PostOrderRequest from "@/domain/request/PostOrderRequest";
import SearchProductRequest from "@/domain/request/SearchProductRequest";
import DeleteRecentSearchWordRequest from "@/domain/request/DeleteRecentSearchWordRequest";
import {memberAuthDataExpiredInterceptor} from "@/api/interceptor/memberAuthDataExpiredInterceptor";
import Membership from "@/domain/order/Membership";
import GetMembershipRequest from "@/domain/request/GetMembershipRequest";

interface ApiResponse<T = any>  {
  code: number;
  message: string;
  result: T;
}

const baseHeaders = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin' : '*',
  'Access-Control-Allow-Methods' : 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
  'Cache-Control': 'no-cache',
  'Pragma' : 'no-cache',
  'Expires' : '-1',
  'x-giftshop-channel-client' : 'user/web'
};

axios.defaults.baseURL = process.env.VUE_APP_API_SERVER_URL;
axios.defaults.timeout = 3000;
axios.defaults.headers = Object.assign({}, baseHeaders);

const apiForNonMember = axios.create({});
const apiForMember = axios.create({});

channelHeaderInterceptor(apiForNonMember);

channelHeaderInterceptor(apiForMember);
memberHeaderInterceptor(apiForMember);
memberAuthDataExpiredInterceptor(apiForMember);

export default {
  getPromotionBannerList(): Promise<AxiosResponse<ApiResponse<Array<PromotionBanner>>>> {
    return apiForNonMember.get(`/banners/promotion`);
  },
  getPromotionBannerById(bannerId: number): Promise<AxiosResponse<ApiResponse<PromotionBannerDetail>>> {
    return apiForNonMember.get(`/banners/promotion/${bannerId}`);
  },
  getThemeBannerList(): Promise<AxiosResponse<ApiResponse<Array<ThemeBanner>>>> {
    return apiForNonMember.get(`/banners/theme`);
  },
  getBestProductList(): Promise<AxiosResponse<ApiResponse<Array<Product>>>> {
    return apiForNonMember.get(`/products/best`);
  },
  getHashtagList(): Promise<AxiosResponse<ApiResponse<Array<Hashtag>>>> {
    return apiForNonMember.get(`/hashtags`);
  },
  getProductsByHashtag(requestBody: GetProductRequest): Promise<AxiosResponse<ApiResponse<GetProductResponse>>> {
    return apiForNonMember.post(`/hashtags/products`, requestBody);
  },
  getProductDetail(id: string): Promise<AxiosResponse<ApiResponse<Product>>> {
    return apiForNonMember.get(`/products/${id}`);
  },
  getGiftReceiverLimit(productId: string): Promise<AxiosResponse<ApiResponse<GetMaxGiftReceiverResponse>>> {
    return apiForNonMember.get(`/products/${productId}/gift-receiver-limit-count`);
  },
  getGiftLimit(productId: number): Promise<AxiosResponse<ApiResponse<GetGiftReceiverLimitResponse>>> {
    return apiForNonMember.get(`/products/${productId}/gift-limit-count`);
  },
  createCellphoneAuthenticationNumber(phoneNumber: string): Promise<AxiosResponse<ApiResponse<null>>> {
    return apiForNonMember.post(`/authorize/tel-no`, {
      telNo: phoneNumber
    });
  },
  checkCellphoneAuthenticationNumber(phoneNumber: string, certificationNumber: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForNonMember.post(`/authorize/tel-no/check`, {
      telNo: phoneNumber,
      certificationNo: certificationNumber,
    });
  },

  // 회원용 API 사용
  searchProduct(requestParam: SearchProductRequest): Promise<AxiosResponse<ApiResponse<GetProductResponse>>> {
    return apiForMember.get(`/search/products`, {
      params: requestParam
    });
  },
  getRecentSearchWord(): Promise<AxiosResponse<ApiResponse<Array<string>>>> {
    return apiForMember.get(`/search/recent-search-keywords`);
  },
  deleteRecentSearchWord(requestParam: DeleteRecentSearchWordRequest): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.delete(`/search/recent-search-keywords`, {
      params: requestParam
    });
  },
  orderProduct(requestBody: PostOrderRequest): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.post(`/orders/reservation`, requestBody);
  },
  getComplexPayments(): Promise<AxiosResponse<ApiResponse<string>>> {
    return apiForMember.get(`/orders/payments/complex`);
  },
  getMembership(params: GetMembershipRequest): Promise<AxiosResponse<ApiResponse<Array<Membership>>>> {
    return apiForMember.get(`/orders/membership`, {params: params});
  },
  getUsableTicketList(params: PageableRequest): Promise<AxiosResponse<ApiResponse<GetTicketResponse>>> {
    return apiForMember.get(`/tickets/usable`, {
      params: params
    });
  },
  getUnusableTicketList(params: PageableRequest): Promise<AxiosResponse<ApiResponse<GetTicketResponse>>> {
    return apiForMember.get(`/tickets/unusable`, {
      params: params
    });
  },
  getTicketDetail(ticketId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.get(`/tickets/${ticketId}`);
  },
  extendValidityOfTicket(ticketId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.post(`/tickets/${ticketId}/validity-extension`);
  },
  refuseTicket(ticketId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.put(`/tickets/${ticketId}/refusal`);
  },
  deleteTicket(ticketId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.delete(`/tickets/${ticketId}`);
  },
  downloadTicket(ticketId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.get(`/tickets/${ticketId}/ticket-image`
        ,{
          responseType: 'blob'
        }
    );
  },
  getOrderList(params: PageableRequest): Promise<AxiosResponse<ApiResponse<GetOrderResponse>>> {
    return apiForMember.get(`/orders`, {
      params: params
    });
  },
  getOrderDetail(orderId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.get(`/orders/${orderId}`);
  },
  cancelOrder(orderId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.post(`/orders/${orderId}/cancel`);
  },
  getCancellationHistory(ticketId: string): Promise<AxiosResponse<ApiResponse<GetTicketCancellationResponse>>> {
    return apiForMember.get(`/tickets/${ticketId}/cancel`);
  },
  cancelTicket(ticketId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.post(`/tickets/${ticketId}/cancel`);
  },
  getRefundHistory(ticketId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return apiForMember.get(`/tickets/${ticketId}/refund`);
  },
}